import React, { useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import {
  getContactPickerPrimaryLabel,
  getContactPickerSecondaryLabel,
} from '../../utils/formatters';

export interface BroadcastPickerContact {
  id: number;
  name: string;
  number: string;
  avatarUrl?: string;
}

export type BroadcastContactListContext = {
  selectedKeys: Set<string>;
  getSelectionKey: (c: BroadcastPickerContact) => string;
  onToggle: (key: string) => void;
};

export interface VirtualizedBroadcastContactListProps {
  contacts: BroadcastPickerContact[];
  selectedKeys: Set<string>;
  getSelectionKey: (c: BroadcastPickerContact) => string;
  onToggle: (key: string) => void;
  /** Max scroll area height in px (list grows with row count up to this cap). */
  maxListHeight?: number;
}

const ROW_HEIGHT = 52;
const DEFAULT_MAX_LIST_HEIGHT = 280;

export const VirtualizedBroadcastContactList: React.FC<VirtualizedBroadcastContactListProps> = ({
  contacts,
  selectedKeys,
  getSelectionKey,
  onToggle,
  maxListHeight = DEFAULT_MAX_LIST_HEIGHT,
}) => {
  const listHeight = useMemo(
    () => Math.min(maxListHeight, Math.max(ROW_HEIGHT, contacts.length * ROW_HEIGHT)),
    [contacts.length, maxListHeight]
  );

  return (
    <Virtuoso<BroadcastPickerContact, BroadcastContactListContext>
      style={{ height: listHeight, width: '100%' }}
      data={contacts}
      context={{ selectedKeys, getSelectionKey, onToggle }}
      computeItemKey={(index, c) => `${getSelectionKey(c)}:${index}`}
      itemContent={(_index, contact, ctx) => {
        const key = ctx.getSelectionKey(contact);
        const primary = getContactPickerPrimaryLabel(contact.name, contact.number);
        const sub = getContactPickerSecondaryLabel(contact.name, contact.number);
        return (
          <div className="contact-item" onClick={() => ctx.onToggle(key)}>
            <input
              type="checkbox"
              checked={ctx.selectedKeys.has(key)}
              onChange={() => ctx.onToggle(key)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="contact-item-avatar">
              <img
                src={contact.avatarUrl || '/user.png'}
                alt={primary}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
              <span className="contact-item-avatar-initial hidden">{primary.charAt(0).toUpperCase()}</span>
            </div>
            <div className="contact-item-info">
              <div className="contact-item-name">{primary}</div>
              {sub ? <div className="contact-item-number">{sub}</div> : null}
            </div>
          </div>
        );
      }}
    />
  );
};
